import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Users, 
  Store, 
  Truck, 
  AlertTriangle, 
  Settings, 
  LogOut,
  ChevronDown,
  Search,
  Bell,
  Menu,
  X,
  BarChart3,
  Key,
  Mail,
  ShieldCheck,
  Wallet,
  Timer,
  ClipboardList,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FASH_LOGO_LIGHT } from '../../constants/brand';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

export function AdminLayout({ children, currentPage, onNavigate }: AdminLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDisputes, setOpenDisputes] = useState(0);
  const [unreadMail, setUnreadMail] = useState(0);
  const [heldStores, setHeldStores] = useState(0);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();

  // Ensure the admin custom claim is on the token (rules + callables fast path)
  useEffect(() => {
    if (!auth.currentUser) return;
    const sync = httpsCallable(functions, 'syncAdminClaims');
    sync({})
      .then((result: any) => {
        if (result?.data?.changed) {
          return auth.currentUser?.getIdToken(true);
        }
        return undefined;
      })
      .catch((err) => console.warn('[AdminLayout] claim sync failed:', err?.message));
  }, []);

  // Live badge: open disputes
  useEffect(() => {
    const q = query(collection(db, 'disputes'), where('status', 'in', ['open', 'investigating']));
    const unsub = onSnapshot(
      q,
      (snap) => setOpenDisputes(snap.size),
      () => setOpenDisputes(0)
    );
    return unsub;
  }, []);

  // Live badge: unread inbound mail
  useEffect(() => {
    const q = query(collection(db, 'inbox'), where('read', '==', false), where('archived', '==', false));
    const unsub = onSnapshot(
      q,
      (snap) => setUnreadMail(snap.size),
      () => setUnreadMail(0)
    );
    return unsub;
  }, []);

  // Live badge: stores with money on hold. Reads the ledger directly rather
  // than the callable so the count stays current while the tab is open.
  useEffect(() => {
    const q = query(collection(db, 'payouts'), where('status', '==', 'held'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const businessIds = new Set<string>();
        snap.forEach((d) => {
          const id = d.data().businessId;
          if (id) businessIds.add(id);
        });
        setHeldStores(businessIds.size);
      },
      () => setHeldStores(0)
    );
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'waitlist'));
    const unsub = onSnapshot(
      q,
      (snap) => setWaitlistCount(snap.size),
      () => setWaitlistCount(0)
    );
    return unsub;
  }, []);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'businesses', label: 'Businesses', icon: Store },
    { id: 'drivers', label: 'Drivers', icon: Truck },
    { id: 'disputes', label: 'Disputes', icon: AlertTriangle, badge: openDisputes || undefined },
    { id: 'payout-holds', label: 'Payout Holds', icon: Wallet, badge: heldStores || undefined },
    { id: 'abandonment', label: 'Abandonment', icon: Timer },
    { id: 'inbox', label: 'Inbox', icon: Mail, badge: unreadMail || undefined },
    { id: 'waitlist', label: 'Waitlist', icon: ClipboardList, badge: waitlistCount || undefined },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    ...(isSuperAdmin ? [{ id: 'team', label: 'Platform Team', icon: ShieldCheck }] : []),
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Close mobile menu when navigating
  const handleNavigate = (page: string) => {
    onNavigate(page);
    setMobileMenuOpen(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "bg-gray-900 text-white flex flex-col transition-all duration-300 z-50",
        // Desktop behavior
        "hidden lg:flex",
        sidebarCollapsed ? "lg:w-20" : "lg:w-64",
        // Mobile behavior - slide in from left
        mobileMenuOpen && "!flex fixed inset-y-0 left-0 w-72"
      )}>
        {/* Logo */}
        <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
          <div className={cn(
            "flex items-center gap-2 transition-all duration-300",
            sidebarCollapsed && "lg:justify-center"
          )}>
            <img src={FASH_LOGO_LIGHT} alt="Fash" className="h-14 object-contain flex-shrink-0" />
            {(!sidebarCollapsed || mobileMenuOpen) && (
              <span className="font-serif text-xl sm:text-2xl text-white">Admin</span>
            )}
          </div>
          {/* Mobile close button */}
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-2 hover:bg-gray-800 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={cn(
                  "w-full flex items-center px-4 sm:px-6 py-3 text-sm font-medium transition-colors relative",
                  isActive 
                    ? "bg-primary text-white" 
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {(!sidebarCollapsed || mobileMenuOpen) && (
                  <>
                    <span className="ml-3">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-error text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {sidebarCollapsed && !mobileMenuOpen && item.badge && (
                  <span className="absolute top-2 right-4 w-2 h-2 bg-error rounded-full" />
                )}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-gray-800">
          <div className="w-full flex items-center space-x-3 text-gray-400">
            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
              <span className="font-medium">
                {(user?.displayName || user?.email || 'A').charAt(0).toUpperCase()}
              </span>
            </div>
            {(!sidebarCollapsed || mobileMenuOpen) && (
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.displayName || 'Admin'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle - Desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:block p-4 border-t border-gray-800 text-gray-500 hover:text-white transition-colors"
        >
          <ChevronDown className={cn(
            "h-5 w-5 mx-auto transition-transform",
            sidebarCollapsed ? "rotate-90" : "-rotate-90"
          )} />
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>

            {/* Search - responsive width */}
            <div className="relative flex-1 max-w-xs sm:max-w-sm lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search..."
                className="pl-9 sm:pl-10 bg-gray-50 border-gray-200 text-sm h-9 sm:h-10"
              />
            </div>

            {/* Right side actions */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <button className="relative p-2 hover:bg-gray-100 rounded-full transition-colors">
                <Bell className="h-5 w-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full" />
              </button>
              <Button variant="outline" size="sm" className="hidden sm:flex" onClick={handleLogout}>
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              <Button variant="outline" size="sm" className="sm:hidden p-2" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
}

